'use client';

import type { ReactNode } from 'react';
import { ArrowRight, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * How a non-compact option lays out: a `card` stacks in the three-column
 * request grid; a `row` is one full-width line, centered like a compact row.
 */
export type PostHogSupportOptionLayout = 'card' | 'row';

/** One row of the support chooser: icon, label, description and arrow. */
export function PostHogSupportOptionContent({
  compact,
  description,
  descriptionId,
  icon: Icon,
  label,
  layout = 'card',
}: {
  compact: boolean;
  description: string;
  descriptionId: string;
  icon: LucideIcon;
  label: string;
  layout?: PostHogSupportOptionLayout;
}): ReactNode {
  return (
    <>
      <span
        className={cn(
          'bg-muted text-muted-foreground flex shrink-0 items-center justify-center rounded-lg',
          compact ? 'size-9' : 'size-10'
        )}
      >
        <Icon className="size-4" strokeWidth={1.75} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block font-medium text-balance',
            compact ? 'text-sm' : 'text-base'
          )}
        >
          {label}
        </span>
        <span
          id={descriptionId}
          className="text-muted-foreground mt-1 block text-xs leading-5 text-pretty sm:text-sm"
        >
          {description}
        </span>
      </span>
      <ArrowRight
        className={cn(
          'text-muted-foreground size-4 shrink-0 rtl:rotate-180',
          !compact && layout === 'card' && 'absolute end-4 top-5'
        )}
        aria-hidden
      />
    </>
  );
}

export function postHogSupportOptionClassName(
  compact: boolean,
  layout: PostHogSupportOptionLayout = 'card'
): string {
  return cn(
    'hover:bg-muted/45 focus-visible:bg-muted/45 focus-visible:ring-ring group relative flex w-full gap-3 text-start outline-none focus-visible:ring-2 focus-visible:ring-inset',
    compact
      ? 'min-h-20 items-center px-4 py-3'
      : layout === 'row'
        ? 'border-border/35 bg-card min-h-20 items-center rounded-xl border p-4 sm:p-5'
        : 'border-border/35 bg-card min-h-28 items-start rounded-xl border p-4 pe-11 sm:min-h-36 sm:flex-col sm:p-5'
  );
}
